import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StreamingConversationAPI, LegalAnalysisAPI, ThreePhaseAnalysisAPI, CaseInfoProcessor, ConversationState, API_CONFIG } from '../api/apiService';

/**
 * 流式聊天钩子 - 处理多轮对话和案件澄清
 * 基于原有script_ja.js的逻辑
 */

export interface StrategyOption {
  id: string;
  name: string;
  description: string;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  type?: 'clarification' | 'confirmation' | 'analysis' | 'strategy_selection' | 'strategy_modification_prompt' | 'normal';
  caseInfo?: Record<string, any>;
  analysisCapability?: string;
  isStreaming?: boolean;
  isStreamingComplete?: boolean;
  files?: Array<{
    id: string;
    name: string;
    size: number;
    type: string;
    url: string;
  }>;
  // 策略选择相关字段
  strategies?: StrategyOption[];
  currentStrategy?: string;
  canModify?: boolean;
  // 文档支持相关字段
  showDocumentSupport?: boolean;
}

export interface StreamingChatState {
  messages: Message[];
  conversation: ConversationState;
  isLoading: boolean;
  isStreaming: boolean;
  streamingContent: Record<string, string>;
  currentStatus: string;
  error: string | null;
  // 三阶段分析相关状态
  useThreePhaseAnalysis?: boolean;
  currentPhase?: number;
  currentPhaseName?: string;
  phaseResults?: Record<number, any>;
  // 策略选择相关状态
  waitingForStrategySelection?: boolean;
  waitingForModificationDecision?: boolean;
  availableStrategies?: StrategyOption[];
  currentStrategy?: string;
  canModifyStrategy?: boolean;
}

export const useStreamingChat = () => {
  const { i18n } = useTranslation();
  
  const [state, setState] = useState<StreamingChatState>({
    messages: [],
    conversation: {
      conversationId: null,
      state: 'initial',
      caseInfo: {},
      completenessScore: 0,
    },
    isLoading: false, // 确保初始状态不是加载中
    isStreaming: false,
    streamingContent: {},
    currentStatus: '',
    error: null,
    // 三阶段分析初始状态 - 设为默认开启
    useThreePhaseAnalysis: true,
    currentPhase: 0,
    currentPhaseName: '',
    phaseResults: {},
    // 策略选择初始状态
    waitingForStrategySelection: false,
    waitingForModificationDecision: false,
    availableStrategies: [],
    currentStrategy: undefined,
    canModifyStrategy: false,
  });

  const streamingMessageRef = useRef<Message | null>(null);
  const currentResponseRef = useRef<Response | null>(null);

  /**
   * 添加消息到对话历史
   */
  const addMessage = useCallback((message: Omit<Message, 'id' | 'timestamp'>) => {
    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substr(2, 9);
    const newMessage: Message = {
      ...message,
      id: `${timestamp}-${randomSuffix}`, // 确保ID唯一性
      timestamp,
      // 初始化流式状态字段
      isStreaming: message.isStreaming ?? false,
      isStreamingComplete: message.isStreamingComplete ?? false,
    };

    // 移除调试信息

    setState(prev => ({
      ...prev,
      messages: [...prev.messages, newMessage],
    }));

    return newMessage;
  }, []);

  /**
   * 更新流式消息内容
   */
  const updateStreamingMessage = useCallback((content: Record<string, string>) => {
    if (!streamingMessageRef.current) {
      return;
    }

    const targetMessageId = streamingMessageRef.current.id;
    // console.log('🔍 updateStreamingMessage被调用:', { targetMessageId, content });
    
    // 保持网络接收的原始顺序，不进行重新排序
    const originalOrder = Object.keys(content);
    
    // 直接按照接收顺序处理内容，不进行排序
    const fullContent = originalOrder
      .map(section => content[section]?.replace(/\n{3,}/g, '\n\n')?.trim() || '')
      .filter(sectionContent => sectionContent.length > 0)
      .join('\n\n');

    setState(prev => {
      const targetMessage = prev.messages.find(msg => msg.id === targetMessageId);
      
      if (!targetMessage) {
        return prev;
      }
      
      if (targetMessage.isStreamingComplete) {
        return prev;
      }

      const updatedMessages = prev.messages
        .filter(msg => msg !== null && msg !== undefined) // 先过滤掉null/undefined
        .map(msg => {
          if (msg.id === targetMessageId) {
            const updatedMsg = { 
              ...msg, 
              content: fullContent,
              // 确保正在流式传输的消息显示为流式状态
              isStreaming: true,
              isStreamingComplete: false
            };
            
            return updatedMsg;
          }
          return msg;
        });

      return {
        ...prev,
        messages: updatedMessages,
        streamingContent: content,
      };
    });
  }, []);

  /**
   * 完成流式消息 - 简化版本，减少复位问题
   */
  const finalizeStreamingMessage = useCallback((conversationData: any) => {
    // 查找并完成最后一个流式消息
    setState(prev => {
      // 找到最后一个isStreaming为true的assistant消息
      let lastStreamingIndex = -1;
      for (let i = prev.messages.length - 1; i >= 0; i--) {
        const msg = prev.messages[i];
        // 安全检查：确保msg不为null
        if (msg && msg.isStreaming && msg.role === 'assistant') {
          lastStreamingIndex = i;
          break;
        }
      }
      
      if (lastStreamingIndex === -1) {
        return prev; // 没有找到流式消息，不做任何操作
      }

      const updatedMessages = prev.messages
        .filter(msg => msg !== null && msg !== undefined) // 先过滤掉null/undefined
        .map((msg, index) => {
          // 只完成最后一个流式消息
          if (index === lastStreamingIndex) {
            return { 
              ...msg, 
              isStreamingComplete: true, 
              isStreaming: false,
              role: 'assistant' as const,
              content: msg.content
                .replace(/\n{3,}/g, '\n\n') // 清理多余换行
                .trim()
            };
          }
          return msg;
        });

      return {
        ...prev,
        messages: updatedMessages,
        isStreaming: false,
      };
    });

    // 清理引用
    if (streamingMessageRef.current) {
      streamingMessageRef.current = null;
    }
  }, []);

  /**
   * 显示澄清问题
   */
  const showClarificationQuestion = useCallback((
    question: string,
    caseInfo: Record<string, any>,
    analysisCapability?: string
  ) => {
    // 重置加载状态，因为现在等待用户输入
    setState(prev => ({
      ...prev,
      isLoading: false,
      isStreaming: false,
      currentStatus: '',
      conversation: {
        ...prev.conversation,
        state: 'clarifying',
        caseInfo,
      }
    }));
    
    // 只保留原始问题内容，不添加额外的提示
    // 分析能力提示和强制分析选项将在ChatMessage组件的蓝色框中显示
    addMessage({
      role: 'assistant',
      content: question,
      type: 'clarification',
      caseInfo,
      analysisCapability: analysisCapability || (i18n.language === 'ja' 
        ? 'より詳細な情報をご提供いただければ、より正確な法律分析をお提供いたします'
        : '如能提供更详细信息，将进行更准确的法律分析'),
    });
  }, [addMessage, i18n.language]);

  /**
   * 显示案件信息确认
   */
  const showCaseInfoConfirmation = useCallback((caseInfo: Record<string, any>) => {
    // console.log('显示案件信息确认，重置isLoading状态');
    
    // 重置加载状态，因为现在等待用户确认
    setState(prev => ({
      ...prev,
      isLoading: false,
      isStreaming: false,
      currentStatus: '',
      conversation: {
        ...prev.conversation,
        state: 'confirmed',
        caseInfo,
      }
    }));
    
    const summary = CaseInfoProcessor.generateCaseInfoSummary(
      caseInfo,
      state.conversation.completenessScore,
      i18n.language === 'ja' 
        ? 'より詳細な情報をご提供いただければ、より正確な法律分析をお提供いたします'
        : '如能提供更详细信息，将进行更准确的法律分析',
      i18n.language
    );
    
    // 第一个AI回复：显示案件信息摘要
    addMessage({
      role: 'assistant',
      content: summary,
      type: 'normal', // 改为normal类型，不包含按钮
      caseInfo,
    });
    
    // 延迟显示第二个AI回复：包含确认选项
    setTimeout(() => {
      const confirmationContent = i18n.language === 'ja' 
        ? '**重要なお知らせ：**\n本サービスは、法律情報および類似判例の参考提供を目的としており、弁護士による法律相談や助言を代替するものではありません。正式な法的判断が必要な場合は、必ず弁護士へご相談ください。\n\n次に進むために、以下のオプションから選択してください：'
        : '基于您提供的信息，我将为您准备详细的法律分析。\n\n**重要通知：**\n本系统仅提供法律信息和相似案例的参考，不构成律师咨询或法律建议。如需正式法律意见，请咨询持牌律师。\n\n请选择以下选项继续：';
      
      addMessage({
        role: 'assistant',
        content: confirmationContent,
        type: 'confirmation', // 这个包含按钮
        caseInfo,
        analysisCapability: i18n.language === 'ja' 
          ? 'より詳細な情報をご提供いただければ、より正確な法律分析をお提供いたします'
          : '如能提供更详细信息，将进行更准确的法律分析',
      });
    }, 800); // 800毫秒延迟
  }, [addMessage, state.conversation.completenessScore, i18n.language]);

  /**
   * 处理流式对话响应
   */
  const handleStreamingConversation = useCallback(async (
    userInput: string, 
    conversationHistory?: Array<{role: string, content: string}>
  ) => {
    try {
      setState(prev => ({ ...prev, isStreaming: true, error: null }));

      // 清理之前的流式消息引用（如果存在）
      if (streamingMessageRef.current) {
        streamingMessageRef.current = null;
      }

      // 创建流式消息
      const streamingMessage = addMessage({
        role: 'assistant',
        content: '',
        isStreaming: true,
      });
      streamingMessageRef.current = streamingMessage;

      // 使用传入的对话历史，如果没有则准备默认历史
      const finalConversationHistory = conversationHistory || state.messages
        .filter(msg => msg && msg.type !== 'clarification' && msg.type !== 'confirmation') // 只包含实际对话
        .map(msg => ({
          role: msg.role,
          content: msg.content
        }));

      // 调用流式对话API，传递完整对话历史
      const response = await StreamingConversationAPI.startConversation(
        userInput,
        state.conversation.conversationId,
        i18n.language,
        finalConversationHistory
      );

      currentResponseRef.current = response;

      let contentBuffer: Record<string, string> = {};
      let conversationData: any = null;

      // 处理流式响应
      await StreamingConversationAPI.processStreamingResponse(
        response,
        // onStatus
        (statusData) => {
          setState(prev => ({ ...prev, currentStatus: statusData.message || '处理中...' }));
        },
        // onContent
        (contentData) => {
          const section = contentData.section || 'default';
          if (contentData.is_section_start) {
            contentBuffer[section] = contentData.content;
          } else {
            if (!contentBuffer[section]) {
              contentBuffer[section] = '';
            }
            contentBuffer[section] += contentData.content;
          }
          updateStreamingMessage(contentBuffer);
        },
        // onComplete
        (completeData) => {
          conversationData = completeData;
          
          // console.log('对话完成，重置状态:', { isStreaming: false, isLoading: false });
          
          // 更新对话状态
          setState(prev => ({
            ...prev,
            conversation: {
              ...prev.conversation,
              conversationId: completeData.conversation_id || prev.conversation.conversationId,
              state: completeData.state || prev.conversation.state,
              caseInfo: { ...prev.conversation.caseInfo, ...(completeData.updated_case_info || completeData.case_info || {}) },
              completenessScore: completeData.completeness_score || prev.conversation.completenessScore,
              rawCaseInfo: completeData.updated_case_info || completeData.case_info,
            },
            isStreaming: false,
            isLoading: false, // 确保重置isLoading状态
            currentStatus: '',
          }));

          // 检查是否需要特殊处理
          const shouldShowConfirmation = completeData &&
            (completeData.completeness_score >= 1.0 ||
             completeData.next_action === 'analyze' ||
             completeData.next_action === 'confirm' ||
             completeData.state === 'confirmed');

          const shouldShowClarification = completeData &&
            completeData.needs_clarification &&
            completeData.clarification_question;

          if (shouldShowConfirmation) {
            // 移除流式消息，显示确认界面
            setState(prev => ({
              ...prev,
              messages: prev.messages.filter(msg => msg.id !== streamingMessage.id),
            }));
            streamingMessageRef.current = null;

            showCaseInfoConfirmation(completeData.updated_case_info || completeData.case_info || state.conversation.caseInfo);
          } else if (shouldShowClarification) {
            // 移除流式消息，显示澄清问题
            setState(prev => ({
              ...prev,
              messages: prev.messages.filter(msg => msg.id !== streamingMessage.id),
            }));
            streamingMessageRef.current = null;

            showClarificationQuestion(
              completeData.clarification_question || contentBuffer.clarification || '',
              completeData.case_info || completeData.updated_case_info || state.conversation.caseInfo,
              completeData.analysis_capability
            );
          } else {
            // 正常完成流式消息
            finalizeStreamingMessage(conversationData);
          }
        },
        // onError
        (error) => {
          setState(prev => ({
            ...prev,
            error,
            isStreaming: false,
            isLoading: false, // 确保重置isLoading状态
            currentStatus: '',
          }));
          if (streamingMessageRef.current) {
            setState(prev => ({
              ...prev,
              messages: prev.messages.filter(msg => msg.id !== streamingMessage.id),
            }));
            streamingMessageRef.current = null;
          }
        }
      );
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : '对话处理失败',
        isStreaming: false,
        isLoading: false, // 确保重置isLoading状态
        currentStatus: '',
      }));

      // 显示简化的澄清问题作为备用
      showClarificationQuestion(
        `**${userInput}**涉及的法律问题，请告诉我们。\n\n⚠️ 流式对话功能暂时出现问题。请告诉我们以下详细信息：\n\n- 具体是什么情况？\n- 涉及的当事人是谁？\n- 你想了解什么法律问题？\n\n如能提供更详细信息，将进行更准确的法律分析。`,
        { event_type: userInput, completeness_score: 0.4 }
      );
    }
  }, [state.conversation.conversationId, state.conversation.caseInfo, addMessage, updateStreamingMessage, finalizeStreamingMessage, showClarificationQuestion, showCaseInfoConfirmation, i18n.language]);

  /**
   * 使用三阶段分析处理用户输入 - 每个阶段创建独立消息块
   */
  const handleThreePhaseAnalysis = useCallback(async (userInput: string) => {
    // 固定三阶段名称映射 - 完全本地化，不依赖服务器数据
    const getFixedPhaseName = (phase: number) => {
      // 安全检查：确保phase是数字
      const phaseNum = typeof phase === 'number' ? phase : parseInt(String(phase).replace(/[^0-9]/g, ''), 10);
      
      const phases: Record<number, {zh: string, ja: string}> = {
        1: { zh: '案例匹配分析', ja: '事例マッチング解析' },
        2: { zh: '深度法律分析', ja: '法的詳細分析' },
        3: { zh: '具体行动建议', ja: '具体的行動提案' }
      };
      
      return phases[phaseNum] 
        ? (i18n.language === 'ja' ? phases[phaseNum].ja : phases[phaseNum].zh)
        : `第${phaseNum}阶段`;
    };

    try {
      setState(prev => ({ 
        ...prev, 
        isStreaming: true, 
        error: null,
        currentStatus: i18n.language === 'ja' 
          ? '三段階解析開始...'
          : '开始三阶段分析...',
        currentPhase: 0,
        phaseResults: {},
      }));

      // 调用三阶段分析API
      const response = await ThreePhaseAnalysisAPI.startThreePhaseAnalysis(
        userInput,
        i18n.language
      );

      currentResponseRef.current = response;

      let currentPhaseMessage: any = null;
      let currentPhaseContent = '';

      // 处理三阶段流式响应
      await ThreePhaseAnalysisAPI.processThreePhaseStreamingResponse(
        response,
        // onPhaseStart
        (phase: number, phaseName: string) => {
          // 安全处理phase参数
          const phaseNum = typeof phase === 'number' ? phase : parseInt(String(phase).replace(/[^0-9]/g, ''), 10);
          // 本地化阶段名称
          const fixedPhaseName = getFixedPhaseName(phaseNum);
          
          // console.log('阶段开始:', { originalPhase: phase, phaseNum, phaseName, fixedPhaseName, language: i18n.language });
          
          // 完成上一个阶段的消息（如果存在）
          if (currentPhaseMessage) {
            setState(prev => {
              const updatedMessages = prev.messages
                .filter(msg => msg !== null && msg !== undefined) // 先过滤掉null/undefined
                .map(msg => {
                  if (msg.id === currentPhaseMessage.id) {
                    return { ...msg, isStreaming: false, isStreamingComplete: true };
                  }
                  return msg;
                });
              
              return {
                ...prev,
                messages: updatedMessages,
              };
            });
            
            // 立即清理上一个阶段的引用
            streamingMessageRef.current = null;
          }
          
          // 为新阶段创建独立的消息块
          currentPhaseContent = '';
          currentPhaseMessage = addMessage({
            role: 'assistant',
            content: i18n.language === 'ja' 
              ? `## 第${phaseNum}段階：${fixedPhaseName}\n\n`
              : `## 第${phaseNum}阶段：${fixedPhaseName}\n\n`,
            type: 'analysis',
            isStreaming: true,
          });
          
          // 更新状态和streamingMessageRef
          setState(prev => ({
            ...prev,
            currentPhase: phaseNum,
            currentPhaseName: fixedPhaseName,
            currentStatus: i18n.language === 'ja' 
              ? `第${phaseNum}段階：${fixedPhaseName} - 解析中...`
              : `第${phaseNum}阶段：${fixedPhaseName} - 分析中...`,
          }));
          streamingMessageRef.current = currentPhaseMessage;
        },
        // onContent
        (phase: number, content: string) => {
          if (currentPhaseMessage && streamingMessageRef.current && currentPhaseMessage.id === streamingMessageRef.current.id) {
            currentPhaseContent += content;
            // 只在当前阶段消息与streamingMessageRef匹配时才更新
            updateStreamingMessage({ 
              default: currentPhaseContent
            });
          }
        },
        // onPhaseComplete
        (phase: number, result: any) => {
          // 安全处理phase参数
          const phaseNum = typeof phase === 'number' ? phase : parseInt(String(phase).replace(/[^0-9]/g, ''), 10);
          
          // 完成当前阶段的消息并更新结果
          if (currentPhaseMessage) {
            setState(prev => {
              const updatedMessages = prev.messages
                .filter(msg => msg !== null && msg !== undefined) // 先过滤掉null/undefined
                .map(msg => {
                  if (currentPhaseMessage && msg.id === currentPhaseMessage.id) {
                    return { ...msg, isStreaming: false, isStreamingComplete: true };
                  }
                  return msg;
                });
              
              return {
                ...prev,
                messages: updatedMessages,
                phaseResults: {
                  ...prev.phaseResults,
                  [phaseNum]: result,
                },
                // 保持流式状态，但更新状态信息
                currentStatus: phaseNum < 3 ? 
                  (i18n.language === 'ja' 
                    ? `第${phaseNum}段階完了、第${phaseNum + 1}段階準備中...`
                    : `第${phaseNum}阶段完成，准备第${phaseNum + 1}阶段...`) :
                  (i18n.language === 'ja' 
                    ? '三段階解析完了間近...'
                    : '三阶段分析即将完成...'),
              };
            });
            
            // 清理当前阶段的streamingMessageRef，但保持流式状态
            streamingMessageRef.current = null;
            currentPhaseMessage = null;
          }
        },
        // onAllComplete
        (results: any) => {
          // 确保所有三阶段分析的消息都被标记为完成
          setState(prev => ({
            ...prev,
            messages: prev.messages
              .filter(msg => msg !== null && msg !== undefined) // 先过滤掉null/undefined
              .map(msg => {
                // 标记所有三阶段分析的消息为完成状态
                if (msg.type === 'analysis' && msg.role === 'assistant') {
                  return { ...msg, isStreaming: false, isStreamingComplete: true };
                }
                return msg;
              }),
            isStreaming: false,
            isLoading: false,
            currentStatus: '', // 立即清空状态，不显示"完成"信息
            currentPhase: 0,
            currentPhaseName: '',
            conversation: { ...prev.conversation, state: 'completed' },
            // 清理策略选择状态
            waitingForStrategySelection: false,
            waitingForModificationDecision: false,
            availableStrategies: [],
            currentStrategy: undefined,
            canModifyStrategy: false,
          }));
          
          // 清理streamingMessageRef
          streamingMessageRef.current = null;
          
          // 后备清理：延迟再次确保所有消息状态正确
          setTimeout(() => {
            setState(prev => ({
              ...prev,
              messages: prev.messages
                .filter(msg => msg !== null && msg !== undefined) // 先过滤掉null/undefined
                .map(msg => {
                  // 强制清理所有可能残留的流式状态
                  if (msg.type === 'analysis' && msg.role === 'assistant' && msg.isStreaming) {
                    return { ...msg, isStreaming: false, isStreamingComplete: true };
                  }
                  return msg;
                }),
              currentStatus: '', // 确保状态清空
            }));
          }, 500); // 缩短到500ms
        },
        // onError
        (error: string) => {
          // 完成当前消息（如果存在）
          if (currentPhaseMessage) {
            setState(prev => ({
              ...prev,
              messages: prev.messages
                .filter(msg => msg !== null && msg !== undefined) // 先过滤掉null/undefined
                .map(msg => {
                  if (currentPhaseMessage && msg.id === currentPhaseMessage.id) {
                    return { ...msg, isStreaming: false, isStreamingComplete: true };
                  }
                  return msg;
                }),
            }));
          }
          
          // 清理所有状态
          setState(prev => ({
            ...prev,
            error,
            isStreaming: false,
            isLoading: false,
            currentStatus: '',
            currentPhase: 0,
            currentPhaseName: '',
          }));
          
          // 清理streamingMessageRef
          streamingMessageRef.current = null;
        },
        // onStrategySelection
        (strategies: StrategyOption[], message: string, canModify: boolean, showDocumentSupport?: boolean) => {
          
          // 添加策略选择消息，确保包含完整的策略信息
          const strategyMessage = addMessage({
            role: 'assistant',
            content: message,
            type: 'strategy_selection',
            strategies: strategies,
            canModify: canModify,
            // 确保消息被标记为已完成，以便正确保存
            isStreaming: false,
            isStreamingComplete: true
          });
          
                            // console.log('✅ 策略选择消息已添加:', strategyMessage);
          
          // 更新状态
          setState(prev => ({
            ...prev,
            waitingForStrategySelection: true,
            availableStrategies: strategies,
            canModifyStrategy: canModify,
            isStreaming: false,
            isLoading: false,
            currentStatus: ''
          }));
          
          // 立即触发保存，确保策略选择消息被保存到历史记录
          // 使用setTimeout确保状态更新完成后再保存
          setTimeout(() => {
                              // console.log('🚀 触发策略选择保存事件');
            // 这里可以触发一个自定义事件来通知父组件保存
            window.dispatchEvent(new CustomEvent('strategySelectionSaved', {
              detail: { strategyMessage, strategies }
            }));
          }, 100);
        },
        // onStrategyModificationPrompt
        (message: string, currentStrategy: string) => {
          // 添加策略修改提示消息
          addMessage({
            role: 'assistant',
            content: message,
            type: 'strategy_modification_prompt',
            currentStrategy: currentStrategy
          });
          
          // 更新状态
          setState(prev => ({
            ...prev,
            waitingForModificationDecision: true,
            currentStrategy: currentStrategy,
            isStreaming: false,
            isLoading: false,
            currentStatus: ''
          }));
        }
      );

    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 
          (i18n.language === 'ja' ? '三段階解析に失敗しました' : '三阶段分析失败'),
        isStreaming: false,
        isLoading: false,
        currentStatus: '',
        currentPhase: 0,
        currentPhaseName: '',
      }));
      
      streamingMessageRef.current = null;
    }
  }, [i18n.language, addMessage, updateStreamingMessage]);

  /**
   * 发送消息
   */
  const sendMessage = useCallback(async (content: string, files?: File[]) => {
    if (!content.trim()) return;



    // 开始发送消息时确保状态正确重置
    setState(prev => ({
      ...prev,
      error: null,
      isLoading: false, // 确保清理之前可能残留的loading状态
    }));

    // 准备完整对话历史（包括当前用户消息）
    const currentConversationHistory = [
      ...state.messages
        .filter(msg => msg && msg.type !== 'clarification' && msg.type !== 'confirmation') // 只包含实际对话
        .map(msg => ({
          role: msg.role,
          content: msg.content
        })),
      // 添加当前用户消息
      {
        role: 'user' as const,
        content: content.trim()
      }
    ];

    // 添加用户消息到界面
    addMessage({
      role: 'user',
      content,
      files: files?.map((file, index) => ({
        id: `file-${Date.now()}-${index}`,
        name: file.name,
        size: file.size,
        type: file.type,
        url: URL.createObjectURL(file),
      })),
    });

    // 如果有文件，先处理文件上传
    if (files && files.length > 0) {
      // TODO: 实现多模态文件处理
    }

    // 所有用户输入都先进入澄清阶段（使用8889端口）
    // 三阶段分析模式只在点击"开始分析"按钮时才生效
    await handleStreamingConversation(content, currentConversationHistory);
  }, [addMessage, state.messages, handleStreamingConversation]);

  /**
   * 确认案件信息并开始分析
   */
  const confirmCaseInfo = useCallback(async () => {
    
    const query = CaseInfoProcessor.generateAnalysisQuery(state.conversation.caseInfo);
    
    addMessage({
      role: 'user',
      content: i18n.language === 'ja' 
        ? '情報に誤りはありません。分析を開始してください。'
        : '信息没有错误。请开始分析',
    });

    setState(prev => ({
      ...prev,
      conversation: { ...prev.conversation, state: 'analyzing' },
      isLoading: true,
    }));

    try {
      // 清理之前的流式消息引用（如果存在）
      if (streamingMessageRef.current) {
        streamingMessageRef.current = null;
      }

      // 根据是否启用三阶段分析选择不同的API
      if (state.useThreePhaseAnalysis) {
        // 使用三阶段分析API（8887端口）
        // 注意：handleThreePhaseAnalysis会自己创建分析消息，所以这里不需要预先创建
        await handleThreePhaseAnalysis(query);
      } else {
        // 使用传统分析API（8888端口）
        // 为传统分析创建分析消息
        const analysisMessage = addMessage({
          role: 'assistant',
          content: '',
          type: 'analysis',
          isStreaming: true,
        });
        streamingMessageRef.current = analysisMessage;

        const response = await LegalAnalysisAPI.streamingAnalyze(query, i18n.language);
        
        let contentBuffer: Record<string, string> = {};

        await StreamingConversationAPI.processStreamingResponse(
          response,
          // onStatus
          (statusData) => {
            setState(prev => ({ 
              ...prev, 
              currentStatus: statusData.message || (i18n.language === 'ja' ? '解析中...' : '分析中...') 
            }));
          },
          // onContent
          (contentData) => {
            const section = contentData.section || 'default';
            if (contentData.is_section_start) {
              contentBuffer[section] = contentData.content;
            } else {
              if (!contentBuffer[section]) {
                contentBuffer[section] = '';
              }
              contentBuffer[section] += contentData.content;
            }
            updateStreamingMessage(contentBuffer);
          },
          // onComplete
          (completeData) => {
            finalizeStreamingMessage(completeData);
            setState(prev => ({
              ...prev,
              conversation: { ...prev.conversation, state: 'completed' },
              isLoading: false,
              currentStatus: '',
            }));
          },
          // onError
          (error) => {
            setState(prev => ({
              ...prev,
              error,
              isLoading: false,
              currentStatus: '',
            }));
          }
        );
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : (i18n.language === 'ja' ? '解析に失敗しました' : '分析失败'),
        isLoading: false,
        currentStatus: '',
      }));
    }
  }, [state.conversation.caseInfo, state.useThreePhaseAnalysis, addMessage, updateStreamingMessage, finalizeStreamingMessage, handleThreePhaseAnalysis, i18n.language]);

  /**
   * 强制开始分析
   */
  const forceStartAnalysis = useCallback(async () => {
    let query = CaseInfoProcessor.generateAnalysisQuery(state.conversation.caseInfo);
    
    const completeness = state.conversation.completenessScore;
    const completenessPercent = Math.round(completeness * 100);
    query += i18n.language === 'ja'
      ? `\n\n【分析説明】この分析は利用者の強制要求による分析で、情報完全度は${completenessPercent}%です。分析結果において情報不完全による結果の精度への影響を明記し、補足が必要な重要情報を提示します。`
      : `\n\n【分析说明】这个分析是用户强制要求的分析，信息完整度是${completenessPercent}%。分析结果中会明确信息不完整对结果准确性的影响，并提出需要补充哪些重要信息。`;

    addMessage({
      role: 'user',
      content: i18n.language === 'ja'
        ? '現在の情報で分析を開始します'
        : '现在用现有信息开始分析',
    });

    // 直接调用确认案件信息方法
    await confirmCaseInfo();
  }, [state.conversation.caseInfo, state.conversation.completenessScore, addMessage, confirmCaseInfo, i18n.language]);

  /**
   * 重新开始对话
   */
  const newChat = useCallback((existingConversationId?: string | null) => {
    setState({
      messages: [],
      conversation: {
        conversationId: existingConversationId || null,  // 支持恢复现有conversation_id
        state: 'initial',
        caseInfo: {},
        completenessScore: 0,
      },
      isLoading: false,
      isStreaming: false,
      streamingContent: {},
      currentStatus: '',
      error: null,
      // 三阶段分析初始状态 - 设为默认开启
      useThreePhaseAnalysis: true,
      currentPhase: 0,
      currentPhaseName: '',
      phaseResults: {},
      // 策略选择初始状态
      waitingForStrategySelection: false,
      waitingForModificationDecision: false,
      availableStrategies: [],
      currentStrategy: undefined,
      canModifyStrategy: false,
    });

    // 清理流式消息引用
    if (streamingMessageRef.current) {
      streamingMessageRef.current = null;
    }
  }, []);

  /**
   * 重新开始对话
   */
  const resetConversation = useCallback(() => {
    setState(prev => ({
      ...prev,
      messages: [],
      conversation: {
        conversationId: null,
        state: 'initial',
        caseInfo: {},
        completenessScore: 0,
      },
      isLoading: false,
      isStreaming: false,
      streamingContent: {},
      currentStatus: '',
      error: null,
      currentPhase: 0,
      currentPhaseName: '',
      phaseResults: {},
      // 策略选择初始状态
      waitingForStrategySelection: false,
      waitingForModificationDecision: false,
      availableStrategies: [],
      currentStrategy: undefined,
      canModifyStrategy: false,
    }));
    
    streamingMessageRef.current = null;
    currentResponseRef.current = null;
  }, []);

  /**
   * 处理策略选择
   */
  const handleStrategySelection = useCallback(async (strategyId: string) => {
    // 不再添加用户策略选择消息到聊天历史，避免显示多余的对话框
    // 直接更新状态并开始第三阶段分析

    setState(prev => ({
      ...prev,
      currentStrategy: strategyId,
      waitingForStrategySelection: false,
      isStreaming: true,
      currentStatus: i18n.language === 'ja' ? '第3段階分析を生成中...' : '正在生成第三阶段分析...'
    }));
    
    // 立即触发保存，确保策略选择状态被保存
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('strategySelectionSaved', {
        detail: { strategyId }
      }));
    }, 100);
    
    // 发送选择到后端（使用可配置的 BASE）
    try {
      await ThreePhaseAnalysisAPI.submitStrategySelection(
        state.conversation.conversationId || 'default',
        strategyId
      );
      // 成功后开始第三阶段分析
      await startPhase3Analysis(strategyId);
    } catch (error) {
      console.error(i18n.language === 'ja' ? '戦略選択の送信に失敗しました:' : '策略选择发送失败:', error);
      setState(prev => ({
        ...prev,
        error: i18n.language === 'ja' ? '戦略選択の送信に失敗しました。再試行してください。' : '策略选择发送失败，请重试',
        isStreaming: false,
        currentStatus: ''
      }));
    }
  }, [state.conversation.conversationId, i18n.language]);

  /**
   * 重新选择策略
   */
  const handleReselectStrategy = useCallback(async () => {
    try {
      // 重置策略选择状态
      setState(prev => ({
        ...prev,
        waitingForModificationDecision: false,
        currentStrategy: undefined,
        isLoading: true,
        currentStatus: i18n.language === 'ja' ? '戦略選択画面を表示中...' : '正在显示策略选择...'
      }));

      // 添加策略选择消息
      const strategyMessage = i18n.language === 'ja' 
        ? "他の戦略方法を選択してください。どの戦略があなたの状況に最も適していると思いますか？"
        : "请选择其他策略方法。您认为哪种策略最适合您的情况？";

      addMessage({
        role: 'assistant',
        content: strategyMessage,
        type: 'strategy_selection',
        strategies: state.availableStrategies || [],
        canModify: true,
        isStreaming: false,
        isStreamingComplete: true
      });

      // 更新状态为等待策略选择
      setState(prev => ({
        ...prev,
        waitingForStrategySelection: true,
        isLoading: false,
        currentStatus: ''
      }));

    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : '重新选择策略失败',
        isLoading: false
      }));
    }
  }, [state.availableStrategies, i18n.language, addMessage]);

  /**
   * 开始第三阶段分析
   */
  const startPhase3Analysis = useCallback(async (selectedStrategy: string) => {
    try {
      // 添加第三阶段开始消息 - 不生成标题，让后端生成
      const phase3Message = addMessage({
        role: 'assistant',
        content: '', // 不设置初始内容，让后端生成完整内容
        type: 'analysis',
        isStreaming: true,
        // 添加当前选择的策略信息，确保保存到历史记录
        currentStrategy: selectedStrategy
      });

      // 设置streamingMessageRef为当前消息
      streamingMessageRef.current = phase3Message;

      // 获取用户查询（最后一个用户消息）
      const userMessages = state.messages.filter(msg => msg.role === 'user');
      const userQuery = userMessages.length > 0 ? userMessages[userMessages.length - 1].content : '';

      // 调用第三阶段分析API（使用可配置 BASE）
      const response = await ThreePhaseAnalysisAPI.startPhase3Analysis(
        userQuery,
        state.phaseResults?.[1]?.metadata?.matched_cases || [],
        state.phaseResults?.[1]?.content || '',
        selectedStrategy,
        i18n.language
      );
      // 处理流式响应
      await processPhase3StreamingResponse(response);
      
    } catch (error) {
      console.error('第三阶段分析失败:', error);
      setState(prev => ({
        ...prev,
        error: i18n.language === 'ja' ? '第3段階分析に失敗しました。再試行してください。' : '第三阶段分析失败，请重试',
        isStreaming: false,
        currentStatus: ''
      }));
    }
  }, [state.messages, state.phaseResults, i18n.language, addMessage]);

  /**
   * 处理第三阶段流式响应
   */
  const processPhase3StreamingResponse = useCallback(async (response: Response) => {
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    
    if (!reader) {
      throw new Error(i18n.language === 'ja' ? 'レスポンスストリームを取得できません' : '无法获取响应流');
    }

    let currentPhaseContent = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              switch (data.type) {
                case 'content':
                  // 使用与第一、第二阶段相同的处理方式
                  if (streamingMessageRef.current) {
                    currentPhaseContent += data.data.content;
                    
                    // 使用 updateStreamingMessage 函数，与第一、第二阶段保持一致
                    updateStreamingMessage({ 
                      default: currentPhaseContent
                    });
                  }
                  break;
                case 'phase_complete':
                  // console.log('第三阶段phase_complete收到:', data.data);
                  finalizeStreamingMessage(data.data);
                  setState(prev => ({
                    ...prev,
                    isStreaming: false,
                    currentStatus: '',
                    currentPhase: 0
                  }));
                  
                  // 立即触发保存，确保第三阶段分析结果被保存到历史记录
                  setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('phase3AnalysisComplete', {
                      detail: { phaseData: data.data }
                    }));
                  }, 100);
                  // 不要立即return，继续处理后续的strategy_selection消息
                  break;
                case 'strategy_selection':
                  // console.log('📋 策略选择消息:', {
                  //   showDocumentSupport: data.data.show_document_support,
                  //   message: data.data.message?.substring(0, 50) + '...',
                  //   rawData: data.data
                  // });
                  // 添加策略选择消息
                  const strategyMessage = addMessage({
                    role: 'assistant',
                    content: data.data.message,
                    type: 'strategy_selection',
                    strategies: data.data.strategies,
                    canModify: data.data.canModify,
                    currentStrategy: data.data.current_strategy,
                    isStreaming: false,
                    isStreamingComplete: true
                  });
                  
                  // 更新状态
                  setState(prev => ({
                    ...prev,
                    waitingForStrategySelection: true,
                    availableStrategies: data.data.strategies,
                    canModifyStrategy: data.data.canModify,
                    isStreaming: false,
                    isLoading: false,
                    currentStatus: ''
                  }));
                  

                  break;
                case 'modification_decision':
                  // console.log('第三阶段modification_decision收到:', data.data);
                  // 注释掉修改决策消息，不再显示"分析完了"对话
                  // const modificationMessage = addMessage({
                  //   role: 'assistant',
                  //   content: data.data.message,
                  //   type: 'strategy_modification_prompt',
                  //   currentStrategy: state.currentStrategy,
                  //   isStreaming: false,
                  //   isStreamingComplete: true
                  // });
                  
                  // 更新状态
                  setState(prev => ({
                    ...prev,
                    waitingForModificationDecision: false, // 直接设置为false，不等待决策
                    isStreaming: false,
                    isLoading: false,
                    currentStatus: ''
                  }));
                  
                  // console.log('✅ 第三阶段修改决策消息已跳过');
                  break;
                case 'end':
                  // console.log('第三阶段end消息收到:', data.data);
                  // 处理流结束信号
                  if (streamingMessageRef.current) {
                    setState(prev => {
                      const updatedMessages = prev.messages.map(msg => {
                        if (msg.id === streamingMessageRef.current?.id) {
                          // console.log('最终消息内容长度:', msg.content.length);
                          // console.log('最终消息内容结尾:', msg.content.slice(-50));
                          return {
                            ...msg,
                            isStreaming: false,
                            isStreamingComplete: true
                          };
                        }
                        return msg;
                      });
                      
                      return {
                        ...prev,
                        messages: updatedMessages,
                        isStreaming: false,
                        currentStatus: ''
                      };
                    });
                  }
                  return;
                case 'error':
                  throw new Error(data.data.message);
              }
            } catch (e) {
              console.warn(i18n.language === 'ja' ? 'ストリーミングデータの解析に失敗しました:' : '解析流式数据失败:', e);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }, [updateStreamingMessage, finalizeStreamingMessage]);

  /**
   * 处理修改决策
   */
  const handleModificationDecision = useCallback(async (decision: 'complete' | 'modify') => {
    if (decision === 'complete') {
      // 完成分析
      setState(prev => ({
        ...prev,
        waitingForModificationDecision: false,
        isStreaming: false,
        isLoading: false,
        currentStatus: ''
      }));
    } else {
      // 重新选择策略
      setState(prev => ({
        ...prev,
        waitingForModificationDecision: false,
        waitingForStrategySelection: true
      }));
    }
    
    // 发送决策到后端（使用可配置 BASE）
    try {
      const response = await fetch(`${API_CONFIG.ENHANCED_ANALYSIS_BASE}/api/analyze/modify-decision`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: state.conversation.conversationId || 'default',
          decision
        })
      });
      if (!response.ok) {
        throw new Error('修改决策发送失败');
      }
    } catch (error) {
      console.error('修改决策发送失败:', error);
      setState(prev => ({
        ...prev,
        error: '修改决策发送失败，请重试'
      }));
    }
  }, [state.conversation.conversationId]);

  /**
   * 加载现有对话状态
   * 用于从聊天历史恢复对话状态
   */
  const loadConversation = useCallback((conversationId: string | null, conversationState?: any) => {
    setState(prev => ({
      ...prev,
      conversation: {
        conversationId: conversationId,
        state: conversationState?.state || 'initial',
        caseInfo: conversationState?.caseInfo || {},
        completenessScore: conversationState?.completenessScore || 0,
      }
    }));
  }, []);

  /**
   * 清除错误
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  /**
   * 启用/禁用三阶段分析模式
   */
  const toggleThreePhaseAnalysis = useCallback((enabled: boolean) => {
    setState(prev => ({
      ...prev,
      useThreePhaseAnalysis: enabled,
    }));
  }, []);

  /**
   * 紧急重置加载状态
   * 用于修复可能卡住的isLoading状态
   */
  const resetLoadingState = useCallback(() => {
    setState(prev => ({
      ...prev,
      isLoading: false,
      isStreaming: false,
      currentStatus: '',
      error: null,
    }));
  }, []);

  /**
   * 设置策略选择状态
   * 用于从聊天历史恢复策略选择状态
   */
  const setStrategySelectionState = useCallback((strategies: StrategyOption[], canModify: boolean, currentStrategy?: string) => {
    setState(prev => ({
      ...prev,
      waitingForStrategySelection: true,
      availableStrategies: strategies,
      canModifyStrategy: canModify,
      currentStrategy: currentStrategy,
    }));
  }, []);

  return {
    // 状态
    messages: state.messages,
    conversation: state.conversation,
    isLoading: state.isLoading,
    isStreaming: state.isStreaming,
    currentStatus: state.currentStatus,
    error: state.error,
    
    // 三阶段分析相关状态
    useThreePhaseAnalysis: state.useThreePhaseAnalysis,
    currentPhase: state.currentPhase,
    currentPhaseName: state.currentPhaseName,
    phaseResults: state.phaseResults,

    // 策略选择相关状态
    waitingForStrategySelection: state.waitingForStrategySelection,
    waitingForModificationDecision: state.waitingForModificationDecision,
    availableStrategies: state.availableStrategies,
    currentStrategy: state.currentStrategy,
    canModifyStrategy: state.canModifyStrategy,

    // 操作
    sendMessage,
    confirmCaseInfo,
    forceStartAnalysis,
    newChat,
    loadConversation,
    clearError,
    addMessage,
    resetConversation,
    handleStrategySelection,
    handleModificationDecision,

    // 三阶段分析相关方法
    toggleThreePhaseAnalysis,
    handleThreePhaseAnalysis,
    resetLoadingState,
    
    // 策略选择相关方法
    setStrategySelectionState,
    handleReselectStrategy,
  };
}; 